/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, NavLink as RouterNavLink } from 'react-router-dom';
import { Box, Flex, HStack, Image, VStack, IconButton, useBreakpointValue } from 'chakra-v3';
import { Text } from '../Text';
import { Button } from '../buttons/Index';
import { CiMenuBurger } from 'react-icons/ci';
import { MenuItem } from './MenuItem';
import type { NavLink } from './MenuItem';
import { MdOutlineRocketLaunch, MdOutlineOpenInNew } from 'react-icons/md';
import { GoBook } from 'react-icons/go';
import { MdClose } from 'react-icons/md';
import { withBaseUrl } from '../../utils/url';
import { useAutoLogin } from '@/features/auth/hooks/useAutoLogin';
import { handleLoginOrDashboardClick } from '@/utils/auth';
import { WorkerRecord } from '@karya/core';
import { Link } from '../Link';

const getNavLinks = (): NavLink[] => {
  const samikshaUrl = import.meta.env.VITE_SAMIKSHA_URL || '/samiksha';
  const isExternal = samikshaUrl.startsWith('http://') || samikshaUrl.startsWith('https://');

  return [
    {
      label: 'Features',
      path: '/#features-section',
      icon: <MdOutlineRocketLaunch size={24} color="currentColor" />,
    },
    {
      label: 'Samiksha',
      path: samikshaUrl,
      icon: <GoBook size={24} color="currentColor" />,
      external: isExternal,
    },
    {
      label: 'Contact Us',
      path: 'https://karya.in/contact',
      icon: <MdOutlineOpenInNew size={24} color="currentColor" />,
      external: true,
    },
  ];
};

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  navLinks: NavLink[];
  navigate: (path: string) => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose, navLinks, navigate }) => (
  <Box
    position="fixed"
    top={0}
    right={0}
    bottom={0}
    width={{ base: '100%', sm: '320px' }}
    maxW="90vw"
    bg="bg.primary"
    borderLeft="1px solid"
    borderColor="rgba(255, 255, 255, 0.1)"
    zIndex={2000}
    transform={isOpen ? 'translateX(0)' : 'translateX(100%)'}
    transition="transform 0.3s ease-in-out"
    boxShadow="-4px 0 20px rgba(0, 0, 0, 0.5)"
    overflowY="auto"
  >
    <Flex
      justify="space-between"
      align="center"
      p={{ base: 3, sm: 4 }}
      borderBottom="1px solid"
      borderColor="rgba(255, 255, 255, 0.1)"
      position="sticky"
      top={0}
      bg="bg.primary"
      zIndex={1}
    >
      <Text fontWeight="600" color="text.primary" fontSize="18px">
        Menu
      </Text>
      <IconButton
        aria-label="Close menu"
        variant="ghost"
        size="sm"
        onClick={onClose}
        _hover={{ opacity: 0.8 }}
        color="text.primary"
        display="flex"
        alignItems="center"
        justifyContent="center"
        minW="40px"
        h="40px"
      >
        <MdClose size={24} color="white" />
      </IconButton>
    </Flex>
    <VStack gap={4} p={{ base: 4, sm: 6 }} alignItems="stretch" pt={6}>
      {navLinks.map((link) => (
        <MenuItem
          key={link.path}
          link={link}
          justifyContent="flex-start"
          onClick={() => {
            onClose();
          }}
        />
      ))}
      <VStack gap={3} mt={2}>
        <Button
          variant="secondary"
          w="100%"
          onClick={() => {
            navigate('/login');
            onClose();
          }}
        >
          Login
        </Button>
      </VStack>
    </VStack>
  </Box>
);

interface MobileOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileOverlay: React.FC<MobileOverlayProps> = ({ isOpen, onClose }) =>
  isOpen ? (
    <Box
      position="fixed"
      inset={0}
      bg="rgba(0, 0, 0, 0.6)"
      zIndex={1999}
      onClick={onClose}
      opacity={isOpen ? 1 : 0}
      transition="opacity 0.3s ease-in-out"
      pointerEvents={isOpen ? 'auto' : 'none'}
    />
  ) : null;

/**
 * Header component providing sticky, responsive navigation.
 *
 * Desktop (≥laptop/1024px): Displays centered nav links with CTA buttons on edges.
 * Tablet (tablet/768px-laptop/1023px): Shows hamburger menu for better space utilization.
 * Mobile (<tablet/768px): Shows hamburger menu that opens a slide-in panel.
 *
 * @remarks
 * Desktop navigation uses flex centering to maintain visual balance
 * while keeping logo and buttons anchored at the edges.
 * Responsive breakpoints use theme breakpoints: mobile (480px), tablet (768px), laptop (1024px), xl (1280px).
 */
const Header: React.FC = () => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isMobile = useBreakpointValue({ base: true, lg: false }) ?? false;
  const navLinks = getNavLinks();

  // Attempt auto-login on component mount
  const [loggedInWorker, setLoggedInWorker] = useState<WorkerRecord | null>(null);
  useAutoLogin({
    onSuccess: (worker) => setLoggedInWorker(worker),
    onFailure: () => setLoggedInWorker(null),
  });

  useEffect(() => {
    // Close mobile menu if resizing to desktop
    if (!isMobile && isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobile, isMobileMenuOpen]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    if (isMobileMenuOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isMobileMenuOpen]);

  const handleCloseMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  const handleNavigate = useCallback(
    (path: string) => {
      void navigate(path);
    },
    [navigate],
  );

  return (
    <>
      <MobileOverlay isOpen={isMobileMenuOpen} onClose={handleCloseMobileMenu} />
      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={handleCloseMobileMenu}
        navLinks={navLinks}
        navigate={handleNavigate}
      />
      <Box
        as="nav"
        bg="bg.primary"
        px={{ base: '16px', md: '32px', lg: '48px' }}
        py={{ base: '12px', md: '20px', lg: '24px' }}
        position="sticky"
        top={0}
        zIndex={1000}
      >
        <Flex align="center" w="100%" gap={{ base: 2, md: 4 }} maxW="1920px" mx="auto" position="relative">
          {/* Logo */}
          <Flex
            as="button"
            cursor="pointer"
            onClick={() => void navigate('/')}
            _hover={{ opacity: 0.9 }}
            _active={{ opacity: 0.8 }}
            transition="opacity 0.2s ease"
            alignItems="center"
            bg="transparent"
            border="none"
            p={0}
            gap={{ base: '6px', md: '8px' }}
            flexShrink={0}
            minWidth={0}
            zIndex={1001}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="120px"
              h="36.217px"
              flexShrink={0}
              position="relative"
              overflow="hidden"
              aspectRatio="120 / 36.22"
            >
              <Image
                src={withBaseUrl('/karya_evals_logo.svg')}
                alt="Karya Logo"
                w="100%"
                h="100%"
                objectFit="contain"
                position="absolute"
                inset={0}
              />
            </Box>
          </Flex>

          {/* Desktop Navigation - Absolutely Centered */}
          {!isMobile && (
            <Flex
              position="absolute"
              left="50%"
              transform="translateX(-50%)"
              justify="center"
              align="center"
              display={{ base: 'none', lg: 'flex' }}
              zIndex={1}
            >
              <HStack gap={{ base: '16px', xl: '20px' }} align="center">
                {navLinks.map((link, index) => (
                  <React.Fragment key={link.path}>
                    {link.label === 'Samiksha' ? (
                      link.external ? (
                        <Box
                          as="button"
                          onClick={() => {
                            if (typeof window !== 'undefined') {
                              window.open(link.path, '_blank', 'noopener,noreferrer');
                            }
                          }}
                          bg="transparent"
                          border="none"
                          p={0}
                        >
                          <Box
                            as="span"
                            display="flex"
                            gap="12px"
                            alignItems="center"
                            px="12px"
                            py="8px"
                            bg="transparent"
                            border="none"
                            color="text.primary"
                            cursor="pointer"
                            _hover={{ opacity: 0.8 }}
                            transition="opacity 0.2s"
                            fontSize="16px"
                            fontWeight={400}
                            lineHeight="1.4"
                            letterSpacing="0.08px"
                            _focusVisible={{ outline: 'none' }}
                          >
                            <Box
                              w="24px"
                              h="24px"
                              flexShrink={0}
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              {link.icon}
                            </Box>
                            <Text whiteSpace="nowrap">{link.label}</Text>
                          </Box>
                        </Box>
                      ) : (
                        <Link asChild variant="plain">
                          <RouterNavLink to={link.path}>
                            <Box
                              as="span"
                              display="flex"
                              gap="12px"
                              alignItems="center"
                              px="12px"
                              py="8px"
                              bg="transparent"
                              border="none"
                              color="text.primary"
                              cursor="pointer"
                              _hover={{ opacity: 0.8 }}
                              transition="opacity 0.2s"
                              fontSize="16px"
                              fontWeight={400}
                              lineHeight="1.4"
                              letterSpacing="0.08px"
                              _focusVisible={{ outline: 'none' }}
                            >
                              <Box
                                w="24px"
                                h="24px"
                                flexShrink={0}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                {link.icon}
                              </Box>
                              <Text whiteSpace="nowrap">{link.label}</Text>
                            </Box>
                          </RouterNavLink>
                        </Link>
                      )
                    ) : (
                      <MenuItem link={link} />
                    )}
                    {index < navLinks.length - 1 && (
                      <Box w="0px" h="16px" flexShrink={0} position="relative">
                        <Box
                          position="absolute"
                          inset="0"
                          borderLeft="1px solid"
                          borderColor="text.primary"
                          opacity={0.3}
                        />
                      </Box>
                    )}
                  </React.Fragment>
                ))}
              </HStack>
            </Flex>
          )}

          {/* Right Side - Buttons */}
          {!isMobile && (
            <Flex
              flexShrink={0}
              gap={{ base: '12px', md: '16px' }}
              align="center"
              display={{ base: 'none', lg: 'flex' }}
              ml="auto"
            >
              <Button
                variant="secondary"
                w={{ base: 'auto', xl: '124px' }}
                px={{ base: '16px', xl: '10px' }}
                onClick={() => handleLoginOrDashboardClick(loggedInWorker, navigate)}
              >
                {loggedInWorker ? 'Dashboard' : 'Login'}
              </Button>
            </Flex>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              aria-label="Open menu"
              variant="ghost"
              size="sm"
              onClick={() => setIsMobileMenuOpen(true)}
              _hover={{ opacity: 0.8 }}
              ml="auto"
              color="text.primary"
              display={{ base: 'flex', lg: 'none' }}
              alignItems="center"
              justifyContent="center"
              minW="40px"
              h="40px"
            >
              <CiMenuBurger size={24} color="white" />
            </IconButton>
          )}
        </Flex>
      </Box>
    </>
  );
};

export default Header;
